import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Tag, Button, Spin, message, Card } from 'antd';
import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getNotification,
  markNotificationRead,
  type Notification,
} from '@/api/notificationApi';
import { useAuth } from '@/hooks/useAuth';

export default function NotificationDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('notification', 'write');

  const [notification, setNotification] = useState<Notification | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getNotification(id);
      setNotification(data);
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMarkRead = async () => {
    if (!id) return;
    try {
      await markNotificationRead(id);
      message.success(t('notification.marked_read'));
      loadData();
    } catch {
      message.error(t('app.error'));
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'blue';
      case 'read': return 'green';
      case 'failed': return 'red';
      case 'sending': return 'orange';
      default: return 'default';
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!notification) return null;

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/notifications')}
        style={{ marginBottom: 16 }}
      >
        {t('app.back')}
      </Button>

      <Card
        title={t('notification.detail_title')}
        extra={
          canWrite && notification.status === 'sent' && (
            <Button icon={<CheckOutlined />} onClick={handleMarkRead}>
              {t('notification.mark_read')}
            </Button>
          )
        }
      >
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('notification.field.title')} span={2}>
            {notification.title}
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.body')} span={2}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{notification.body}</div>
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.channel')}>
            <Tag>{t(`notification.channel.${notification.channel}`, notification.channel)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.status')}>
            <Tag color={statusColor(notification.status)}>
              {t(`notification.status.${notification.status}`, notification.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.event_type')}>
            {notification.event_type}
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.sent_at')}>
            {notification.sent_at ? new Date(notification.sent_at).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.read_at')}>
            {notification.read_at ? new Date(notification.read_at).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('notification.field.retry_count')}>
            {notification.retry_count}
          </Descriptions.Item>
          {notification.related_entity_type && (
            <Descriptions.Item label={t('notification.field.related_entity')} span={2}>
              {notification.related_entity_type}: {notification.related_entity_id}
            </Descriptions.Item>
          )}
          {notification.error_message && (
            <Descriptions.Item label={t('notification.field.error_message')} span={2}>
              <Tag color="red">{notification.error_message}</Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('common.created_at')}>
            {new Date(notification.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.updated_at')}>
            {new Date(notification.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
}
